// @ts-nocheck
// bunzl check - new styles from foundation come back later
import { lazyImageStyles } from "@store/Themes/common/mixins/lazyload";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import PageBreadcrumbs from "@insite/content-library/Components/PageBreadcrumbs";
import LazyImage from "@insite/mobius/LazyImage";
import { LazyImageProps } from "@insite/mobius/LazyImage/LazyImage";
import breakpointMediaQueries, { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CategoryNameStyles } from "@store/Overrides/Widgets/Category/CategoryName";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { getSettingsCollection } from "@toolkit/Framework/Store/Context/ContextSelectors";
import { siteWidthStyles } from "@toolkit/Themes/common/mixins";
import parse from "html-react-parser";
import React, { useContext } from "react";
import { useSelector } from "react-redux";
import { css } from "styled-components";

const CategoryName = getWidgetComponent("Category/CategoryName");

const enum fields {
    hideBreadcrumbs = "hideBreadcrumbs",
    titleColor = "titleColor",
    hasBottomBorder = "hasBottomBorder",
    hasCustomOverlay = "hasCustomOverlay",
    customOverlayImage = "customOverlayImage",
    customOverlayPosition = "customOverlayPosition",
    customOverlayAligment = "customOverlayAligment",
    overlay = "overlay",
    imageOverlay = "imageOverlay",
    aligment = "aligment",
}

interface Props extends WidgetProps {
    fields: {
        [fields.hideBreadcrumbs]: boolean;
        [fields.titleColor]: string;
        [fields.hasBottomBorder]: boolean;
        [fields.hasCustomOverlay]: boolean;
        [fields.customOverlayImage]: string;
        [fields.customOverlayPosition]: number;
        [fields.customOverlayAligment]: "left" | "right";
        [fields.overlay]: boolean;
        [fields.imageOverlay]: string;
        [fields.aligment]: "flex-start" | "center" | "flexp-end";
    };
}

export interface CategoryDetailsHeaderStyles {
    wrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    overlayWrapper?: InjectableCss;
    image?: LazyImageProps;
    titleWrapper?: InjectableCss;
    title?: CategoryNameStyles;
    contentWrapper?: InjectableCss;
    textWrapper?: InjectableCss;
    customOverlayImage?: LazyImageProps;
}

export const categoryDetailsHeaderStyles: CategoryDetailsHeaderStyles = {
    wrapper: {
        css: css`
            background-color: ${getColor("common.accentLighter")};
        `,
    },
    imageWrapper: {
        css: css`
            position: relative;
            height: 57vh;

            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            height: 32vh;
                        `,
                    ],
                    mediaOptions.max,
                )}
        `,
    },
    image: {
        css: css`
            height: 57vh;
            position: relative;
            width: 100%;
            padding-top: 25%;
            ${lazyImageStyles()}

            img {
                height: 100%;
                position: absolute;
                top: 50%;
                width: auto;
                min-width: 100%;
                min-height: 100%;
                max-width: none;
                transition: transform 1s cubic-bezier(0.215, 0.61, 0.355, 1);
                transform: translateY(-50%) scale(1);
            }

            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            height: 32vh;
                        `,
                    ],
                    mediaOptions.max,
                )}
        `,
    },
    titleWrapper: {
        css: css`
            position: absolute;
            top: 50%;
            right: 0;
            left: 50%;
            transform: translateY(-50%);
            padding: 2.5em 3.75em;
            background-color: ${getColor("common.backgroundRgba")};

            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            left: 30%;
                            padding: 1em;
                        `,
                    ],
                    mediaOptions.max,
                )}
        `,
    },
    contentWrapper: {
        css: css`
            ${siteWidthStyles()}
            padding-top: 1.75em;
            padding-bottom: 2.75em;
            text-align: center;

            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding-top: 0.75em;
                            padding-bottom: 1.75em;
                        `,
                    ],
                    mediaOptions.max,
                )}

            body {
                background-color: transparent;
            }
        `,
    },
    textWrapper: {
        css: css`
            max-width: ${pxToRem(930)};
            margin: 0 auto;
        `,
    },
};

const styles = categoryDetailsHeaderStyles;

const CategoryDetailsHeader: React.FC<Props> = ({ fields }) => {
    const category = useContext(CategoryContext);

    const defaultImagePath = useSelector(
        (state: ApplicationState) =>
            getSettingsCollection(state).extensionCatalogSettings?.notFoundCategoryLargeImagePath,
    );

    if (!category) {
        return null;
    }

    const imagePath = category.largeImagePath || defaultImagePath;

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.imageWrapper}>
                <LazyImage src={imagePath} {...styles.image} />
                <StyledWrapper {...styles.titleWrapper}>
                    <CategoryName />
                </StyledWrapper>
            </StyledWrapper>
            {!fields.hideBreadcrumbs ? <PageBreadcrumbs /> : null}
            {category.htmlContent ? (
                <StyledWrapper {...styles.contentWrapper}>
                    <StyledWrapper {...styles.textWrapper}>{parse(category.htmlContent, parserOptions)}</StyledWrapper>
                </StyledWrapper>
            ) : null}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: CategoryDetailsHeader,
    definition: {
        group: "Categories",
        displayName: "Category Details Header",
        fieldDefinitions: [
            {
                name: fields.hideBreadcrumbs,
                displayName: "Hide categories breadcrumbs",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                fieldType: "General",
                name: fields.titleColor,
                displayName: "Title Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
            },
            {
                name: fields.overlay,
                displayName: "Add an Overlay",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                fieldType: "General",
                name: fields.imageOverlay,
                displayName: "Image Color Overlay",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.overlay,
            },
            {
                fieldType: "General",
                name: fields.aligment,
                displayName: "Title Aligment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                defaultValue: "center",
                hideEmptyOption: true,
            },
        ],
    },
};

export default widgetModule;
